import type { RecordDto } from '@halo/shared';
import {
  dateOrdinal,
  formatDayMonth,
  formatFullDate,
  monthKeyOf,
  weekStartOf,
  parseDayMonth,
  type CalendarDate,
} from '../dates';
import {
  applyChanges,
  compactData,
  readNamedShares,
  readNumber,
  readOptionalString,
  readString,
  readStringList,
  type NamedShare,
  type RecordData,
} from './parsing';

export const VIDEO_TYPES = ['Mở', 'Live'] as const;
export type VideoType = (typeof VIDEO_TYPES)[number];

/** Trạng thái của video TRÊN YOUTUBE. «xoa» = đã xoá trên YouTube; bản ghi và số liệu vẫn giữ. */
export const VIDEO_STATUSES = ['public', 'backup', 'an', 'xoa'] as const;
export type VideoStatus = (typeof VIDEO_STATUSES)[number];

export const VIDEO_STATUS_LABELS: Record<VideoStatus, string> = {
  public: 'Đã public',
  backup: 'Back up',
  an: 'Đã ẩn',
  xoa: 'Đã xoá',
};

/** Tên thiết bị CỐ ĐỊNH — ô tự do từng sinh ra rác («Philippines -», «a») không cộng được. */
export const DEVICE_NAMES = ['Điện thoại di động', 'Máy tính', 'Máy tính bảng', 'TV'] as const;
export const MAX_COUNTRIES = 3;

/**
 * Bản ghi cũ không có năm. App chỉ mới chạy trong năm 2026 nên coi chúng là 2026 — KHÔNG lấy
 * năm hiện tại: sang năm mới mà lấy năm hiện tại là toàn bộ video cũ tự nhảy năm.
 */
export const LEGACY_DEFAULT_YEAR = 2026;

export type PendingMilestone = '24h' | '48h' | null;

/** Các chỉ số dạng SỐ của video — cùng một khoá dùng cho ô nhập, sửa tại ô và cột bảng. */
export const VIDEO_NUMERIC_FIELDS = [
  'traffic24h',
  'traffic48h',
  'ctr24h',
  'ctr48h',
  'impressions24h',
  'impressions48h',
  'retention45s',
  'averagePercentViewed',
  'rpm',
] as const;
export type VideoNumericField = (typeof VIDEO_NUMERIC_FIELDS)[number];

export interface Video {
  recordId: number;
  owner: string | null;
  /** Mã ổn định «v12@tên-đăng-nhập». Bản ghi rất cũ có thể là «v12» hoặc không có. */
  code: string;
  title: string;
  /** Mã kênh («k3@user»). Có thể là mã CHẾT (kênh đã xoá) — luôn tra qua danh sách kênh. */
  channelCode: string | undefined;
  publishedOn: CalendarDate | null;
  /** Thời lượng như người dùng gõ: «1:13:05», «9:51» hoặc số giây. */
  duration: string;
  type: VideoType;
  status: VideoStatus;
  url: string;
  /** Ảnh gốc — ĐƯỜNG DẪN do máy chủ cấp (ảnh không nằm trong dữ liệu bản ghi). */
  thumbnailUrl: string | null;
  /** Ảnh đã sửa (sau khi CTR thấp) — data-URL nằm ngay trong bản ghi. */
  editedThumbnail: string | null;
  /** Ngày đổi thumbnail, «DD/MM/YYYY». */
  thumbnailChangedOn: string | null;
  traffic24h: number | null;
  traffic48h: number | null;
  ctr24h: number | null;
  ctr48h: number | null;
  impressions24h: number | null;
  impressions48h: number | null;
  retention45s: number | null;
  /** AVD — thời lượng xem trung bình, «9:51». */
  averageViewDuration: string;
  averagePercentViewed: number | null;
  rpm: number | null;
  tracks: string[];
  countries: NamedShare[];
  devices: NamedShare[];
  /** Ghi chú sự kiện — chỉ có ở dữ liệu cũ. */
  eventNote: string | undefined;
}

/** Tên trường trong bản ghi đang lưu ↔ tên trong mô hình. Đổi tên ở MỘT chỗ này. */
const NUMERIC_KEYS: Record<VideoNumericField, string> = {
  traffic24h: 't24',
  traffic48h: 't48',
  ctr24h: 'c24',
  ctr48h: 'c48',
  impressions24h: 'x24',
  impressions48h: 'x48',
  retention45s: 'ret',
  averagePercentViewed: 'apv',
  rpm: 'rpm',
};

function readPublishedOn(data: RecordData): CalendarDate | null {
  const dayMonth = parseDayMonth(readString(data, 'd'));
  if (!dayMonth) return null;
  return { ...dayMonth, year: readNumber(data, 'y') || LEGACY_DEFAULT_YEAR };
}

function readEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

export function parseVideo(record: RecordDto): Video {
  const data = record.data;
  const editedThumbnail = readOptionalString(data, 'thumb2') ?? null;
  return {
    recordId: record.id,
    owner: record.owner,
    code: readString(data, 'id') || `legacy:${record.id}`,
    title: readString(data, 'title'),
    channelCode: readOptionalString(data, 'ch'),
    publishedOn: readPublishedOn(data),
    duration: readString(data, 'dur'),
    type: readEnum(data.type, VIDEO_TYPES, 'Mở'),
    status: readEnum(data.st, VIDEO_STATUSES, 'public'),
    url: readString(data, 'url'),
    thumbnailUrl: record.images.thumb ?? null,
    editedThumbnail,
    thumbnailChangedOn: readOptionalString(data, 'thumbd') ?? null,
    traffic24h: readNumber(data, NUMERIC_KEYS.traffic24h),
    traffic48h: readNumber(data, NUMERIC_KEYS.traffic48h),
    ctr24h: readNumber(data, NUMERIC_KEYS.ctr24h),
    ctr48h: readNumber(data, NUMERIC_KEYS.ctr48h),
    impressions24h: readNumber(data, NUMERIC_KEYS.impressions24h),
    impressions48h: readNumber(data, NUMERIC_KEYS.impressions48h),
    retention45s: readNumber(data, NUMERIC_KEYS.retention45s),
    averageViewDuration: readString(data, 'apd'),
    averagePercentViewed: readNumber(data, NUMERIC_KEYS.averagePercentViewed),
    rpm: readNumber(data, NUMERIC_KEYS.rpm),
    tracks: readStringList(data, 'tracks'),
    countries: readNamedShares(data, 'geo'),
    devices: readNamedShares(data, 'dev'),
    eventNote: readOptionalString(data, 'evt'),
  };
}

/** Mốc nào còn thiếu số: chưa có traffic 24h → «24h»; có rồi mà chưa có 48h → «48h». */
export function pendingMilestone(
  video: Pick<Video, 'traffic24h' | 'traffic48h'>,
): PendingMilestone {
  if (video.traffic24h === null) return '24h';
  return video.traffic48h === null ? '48h' : null;
}

/** Mô hình → TOÀN BỘ các khoá mà mô hình quản lý (chưa trộn với dữ liệu đang lưu). */
function toVideoData(video: Video): RecordData {
  const numeric = Object.fromEntries(
    VIDEO_NUMERIC_FIELDS.map((field) => [NUMERIC_KEYS[field], video[field]]),
  );
  return {
    id: video.code,
    title: video.title,
    ch: video.channelCode,
    d: video.publishedOn ? formatDayMonth(video.publishedOn) : '',
    y: video.publishedOn?.year,
    dur: video.duration,
    type: video.type,
    st: video.status,
    url: video.url,
    thumb2: video.editedThumbnail,
    thumbd: video.thumbnailChangedOn,
    ...numeric,
    apd: video.averageViewDuration,
    tracks: video.tracks,
    // KHÔNG cắt còn 3 quốc gia ở đây: cắt là việc của form nhập (có báo trước cho người dùng).
    geo: video.countries.length ? video.countries : null,
    dev: video.devices.length ? video.devices : null,
    evt: video.eventNote,
    need: pendingMilestone(video),
  };
}

/**
 * Mô hình → nội dung bản ghi để gửi lên. Ảnh gốc KHÔNG nằm ở đây (đi qua `images` của thao tác
 * ghi).
 *
 * Có `stored` (đang SỬA một bản ghi): chỉ những khoá người dùng thật sự đổi mới được ghi đè —
 * xem `applyChanges`. Không có `stored` (TẠO MỚI): ghi đầy đủ.
 */
export function serializeVideo(video: Video, stored?: RecordDto): RecordData {
  const next = toVideoData(video);
  if (!stored) return compactData(next);
  return compactData(applyChanges(stored.data, toVideoData(parseVideo(stored)), next));
}

/** Ảnh ĐANG CHẠY trên YouTube: đã đổi thumbnail thì bản sửa mới là cái người xem thấy. */
export function displayedThumbnail(
  video: Pick<Video, 'editedThumbnail' | 'thumbnailUrl'>,
): string | null {
  return video.editedThumbnail ?? video.thumbnailUrl;
}

export function publishedLabel(video: Pick<Video, 'publishedOn'>): string {
  return video.publishedOn ? formatFullDate(video.publishedOn) : '—';
}

/** Khoá tháng «MM/YYYY» của video; không có ngày đăng → null. */
export function videoMonthKey(video: Pick<Video, 'publishedOn'>): string | null {
  return video.publishedOn ? monthKeyOf(video.publishedOn) : null;
}

/** Khoá tuần = «DD/MM/YYYY» của Chủ nhật tuần đăng (tuần chạy Chủ nhật → Thứ 7). */
export function videoWeekKey(video: Pick<Video, 'publishedOn'>): string | null {
  return video.publishedOn ? formatFullDate(weekStartOf(video.publishedOn)) : null;
}

function codeSequence(code: string): number {
  const match = /^[a-z]+(\d+)/i.exec(code);
  return match ? Number(match[1]) : 0;
}

/** Thứ tự lưu trữ: cũ → mới theo ngày đăng, trùng ngày thì theo số trong mã. */
export function compareVideosOldestFirst(a: Video, b: Video): number {
  const byDate =
    (a.publishedOn ? dateOrdinal(a.publishedOn) : 0) -
    (b.publishedOn ? dateOrdinal(b.publishedOn) : 0);
  return byDate || codeSequence(a.code) - codeSequence(b.code) || a.recordId - b.recordId;
}

/**
 * Mã cho video/kênh mới: «<tiền tố><số>@<tên đăng nhập>». Phần «@tên» là bắt buộc: mỗi người
 * chỉ nhìn thấy bản ghi của mình nên hai nhân viên từng cùng sinh ra «k1» — Giám đốc (thấy cả
 * hai) bị trộn hai kênh vào một rổ.
 */
export function nextLocalCode(
  prefix: string,
  existingCodes: readonly string[],
  username: string,
): string {
  const highest = existingCodes.reduce((max, code) => {
    const match = new RegExp(`^${prefix}(\\d+)`).exec(code);
    return match ? Math.max(max, Number(match[1])) : max;
  }, 0);
  return `${prefix}${highest + 1}@${username}`;
}
